import { APIv2Fetch } from 'classy-api-proxies/apiv2'
import {
  OrganizationChannelPageResponse,
  OrganizationChannelEventResponse,
  CreateOrganizationChannelEventPayload,
  OrganizationChannel,
} from './integrationChannels.model'

export interface GetOrganizationChannelsParams {
  organizationId: string
}
/**
 * Get a list of third-party services integrated with an Organization (ie Meta CAPI).
 *
 * @see https://dev-docs.classy-test.org/apiv2/#tag/Organization-Channel/operation/listOrganizationChannel
 *
 */
export async function getOrganizationChannels({
  organizationId,
}: GetOrganizationChannelsParams): Promise<OrganizationChannelPageResponse> {
  const response = await APIv2Fetch(`/organizations/${organizationId}/channels?per_page=100`, {
    method: 'GET',
  })

  const channels: OrganizationChannelPageResponse = await response.json()

  return channels
}

/**
 * The only integration channel we currently support is MetaCapi, everything else is an
 * AnalyticsServices Setting (google_analytics_4, `google_tag_manager`, `facebook_pixel`,
 * `tealium` at time of writing).
 * This function does the following:
 *   - Filter out any other integration channels other than MetaCapi as they could
 *     contain sensitive data.
 *   - Remove all sensitive data from the additional_configurations object as MetaCapi
 *     only needs the org id to send the event to APIv2 where it will be fully processed.
 */
export async function getSanitizedOrganizationChannels({
  organizationId,
}: GetOrganizationChannelsParams): Promise<OrganizationChannel[]> {
  const response = await getOrganizationChannels({ organizationId })
  const channels = response.data
  const sanitizedChannels = channels
    .filter((channel) => channel.channel_name === 'MetaCapi')
    .map((channel) => {
      if (channel.additional_configurations) {
        channel.additional_configurations = {}
      }
      return channel
    })

  return sanitizedChannels
}

/**
 * Post an event (ie. analytics.page, analytics.track) to a third-party service
 * integrated with the Organization (currently only implemented for MetaCapi)
 *
 * createOrganizationChannelEvent
 * @see https://dev-docs.classy-test.org/apiv2/#tag/ChannelEvent/operation/createOrganizationChannelEvent
 *
 */
export async function createChannelEvent(
  organization_id: string,
  body: CreateOrganizationChannelEventPayload,
): Promise<OrganizationChannelEventResponse> {
  const response = await fetch(`/api/organizations/${organization_id}/channel-events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  const json: OrganizationChannelEventResponse = await response.json()

  return json
}
